import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile, isSafari } from 'react-device-detect';
import axios from 'axios';
import styled from 'styled-components';
import { AddPhotoAlternate, Delete, OndemandVideo } from '@material-ui/icons';
import { Icon, CircularProgress } from '@material-ui/core';
import { ImageOptions } from '../../../CampaignEdit/CampaignEdit.styles';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg, generateRandomString } from './utils';
import { get } from 'lodash';

enum MediaFileSize {
  Short = 'short',
  Narrow = 'narrow',
  Default = 'default',
  Poll = 'poll',
  Package = 'package'
}

const StyledLoading = styled(CircularProgress)`
  && {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImageWrapper = styled.div<{ size: MediaFileSize, isCropping: Boolean, file: string }>`
  width: 100%;
  max-width: ${({ size }) => {
    switch (size) {
      case MediaFileSize.Narrow:
        return '300px';
      default:
        return '100%';
    }
  }};
  border: 1px dashed #A0AEBE;
  background-color: #F8FAFD;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: ${({ isCropping }) => isCropping ? '60px' : '0'};
  padding-bottom: ${({ file }) => file ? '60px' : '0'};
  && img {
    height: auto;
    width: 100%;
  }
  > label {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: ${({ size }) => {
      switch (size) {
        case MediaFileSize.Short:
          return '90px';
        case MediaFileSize.Narrow:
          return '250px';
        case MediaFileSize.Package:
          return '170px';
        default:
          return '340px';
      }
    }};
    > input {
      position: absolute;
      width: 100%;
      height: 100%;
    }
  }
  > video {
    width: 728px;
    height: 410px;
    @media screen and (max-width: 690px) {
      width: 100%;
      height: auto;
    }
  }
  .spinnerWrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${({ size }) => {
      switch (size) {
        case MediaFileSize.Short:
          return '90px';
        case MediaFileSize.Narrow:
          return '250px';
        case MediaFileSize.Package:
          return '170px';
        default:
          return '340px';
      }
    }};
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  .icon {
    height: 45px;
    width: 45px;
    color: #062B54;
    margin: 0 30px 0 0;
    svg {
      width: 100%;
      height: 100%;
    }
  }
  > div {
    h4 {
      margin: 0;
    }
    span {
      color: #2A71D4;
      font-size: 12px;
      font-weight: bold;
      text-decoration: underline;
    }
  }
`;

const StyledCrop = styled(ReactCrop)`
  width: 100%;
  height: 100%;
  > div {
    height: 100%;
    width: 100%;
  }
`;

const CropOptions = styled(ImageOptions)`
  bottom: -60px;
`;

const Error = styled.p`
  color: #f44336;
  bottom: -10px;
  position: absolute;
`;

export const FormikFileField = ({ field, form, isVideo = false, size = MediaFileSize.Default, path = '/api/campaign/campaign/create-media/' }: any) => {
  const acceptImages = 'image/png, image/jpeg';
  const acceptVideo = 'video/*';
  const [publicUrl, setPublicUrl] = useState();
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [crop, setCrop] = useState({ unit: '%', width: 20, aspect: 16 / 9 });
  const [upImg, setUpImg] = useState<any>(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const imgRef = useRef(null);
  const fileUpload = useRef(null);
  useEffect(() => {
    switch (size) {
      case MediaFileSize.Short:
        setCrop({ unit: '%', width: 100, aspect: 728 / 90 });
        break;
      case MediaFileSize.Narrow:
        setCrop({ unit: '%', width: 100, aspect: 300 / 250 });
        break;
       case MediaFileSize.Default:
        setCrop({ unit: '%', width: 100, aspect: 300 / 250 });
        break;
       case MediaFileSize.Poll:
        setCrop({ unit: '%', width: 100, aspect: 360 / 202.5 });
        break;
       case MediaFileSize.Package:
        setCrop({ unit: '%', width: 100, aspect: 300 / 170 });
        break;
      default:
        break;
    }
  }, [])

  const postImage = (file: any) => {
    const { campaignType } = form.values.content || form.values;
    if (file) {
      axios.post(`${process.env.REACT_APP_BASE_URL}${path}`, {
        media_type: file.type,
        file_name: generateRandomString(10),
        campaign_type: campaignType || '123'
        // ...(campaignType && {campaign_type: campaignType}),
      }).then(res => {
        setIsFileLoading(true);
        const { data } = res;
        setPublicUrl(data.public_url);
        sessionStorage.setItem('publicUrl', data.public_url);
        const formData = new FormData();
        Object.entries(data.fields).forEach(entry => {
          const [key, value] = entry;
          //@ts-ignore
          formData.append(key, value);
        });
        formData.append('file', file);

        axios.post(data.public_url, formData).then(res => {
          form.setFieldValue(field.name, data.fields.key);
          setIsFileLoading(false);
        }).catch(err => {
          console.log(err)
        })
      }).catch(err => {
        console.log('there is some err', err);
      });
    }
  }

  const onFileSelect = (e: any) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const reader = new FileReader();
      const file = e.target.files[0];
      if (isVideo) {
        postImage(file)
      } 
      if (!isVideo) {
        reader.addEventListener("load", () => setUpImg(reader.result));
        reader.readAsDataURL(file);
      }
    }
  };

  const removeFile = () => {
    form.setFieldValue(field.name, null);
    setUpImg(null)
  }

  const generateImage = async () => {
    const image = new Image();
    image.src = upImg;
    // @ts-ignore
    const { width: refWidth, height: refHeight } = imgRef.current;
    image.width = refWidth
    image.height = refHeight;
    const test = await getCroppedImg(image, crop, new Date())
    postImage(test);
    setUpImg(null);
  }

  const replaceFile = () => {
    removeFile();
    setTimeout(() => {
      // @ts-ignore
      fileUpload.current.click();
    }, 500)
  }

  const onLoad = useCallback((img: any) => {
    imgRef.current = img;
  }, []);

  const getVideoBlob = (videoUrl: string) => {
    axios.get(videoUrl, { responseType: 'arraybuffer'}).then((res) => {
      let blob = new Blob([res.data], { type: 'video/mp4' });
      let url = URL.createObjectURL(blob);
      setVideoBlobUrl(url);
    })
  }

  useEffect(() => {
    const { campaignType } = form.values.content || form.values;
    if (isSafari && campaignType === 'V' && field.value) {
      const url = field.value.includes('http') ? field.value : `${sessionStorage.getItem('publicUrl')}/${field.value}`;
      getVideoBlob(url);
    }
  }, [field.value])

  const isInvalid = get(form.errors, field.name) && (get(form.touched, field.name) || form.submitCount > 0);
  return (
    <ImageWrapper size={size} isCropping={upImg && !isVideo} file={field.value}>
      {upImg && !isVideo && <>
        <StyledCrop
          src={upImg}
          onImageLoaded={onLoad}
          // @ts-ignore
          crop={crop}
          onChange={(c: any, percentCrop: any) => setCrop(c)}
          style={{ width: '100%', height: '100%' }}
        />
        <CropOptions>
          <p onClick={generateImage}>Crop & Save</p>
        </CropOptions>
      </>}
      {!isFileLoading &&
        <>
          {isVideo ?
            (field.value && 
              <video 
                width="100%" 
                height="100%" 
                autoPlay 
                muted 
                loop 
                src={(isSafari && videoBlobUrl) ? videoBlobUrl : field.value.includes('http') ? field.value : `${sessionStorage.getItem('publicUrl')}/${field.value}`}>
              </video>)
            : (field.value && <img src={field.value.includes('http') ? field.value : `${sessionStorage.getItem('publicUrl')}/${field.value}`} alt={field.value} />)
          }
        </>
      }
      {isFileLoading ?
        <div className="spinnerWrapper"><StyledLoading /></div>
        :
        <>
          {!field.value && !upImg &&
            <label htmlFor={field.name + 'image'}>
              <FlexWrapper>
                <Icon className="icon">
                  {isVideo 
                    ? <OndemandVideo />
                    : <AddPhotoAlternate />
                  }
                </Icon>
                <div>
                  {isMobile ? 
                    <>
                      <h4>Click here to upload your media</h4>
                    </>
                    :
                      <>
                        <h4>Drag & Drop your media</h4>
                        <span>or upload from your computer</span>
                      </>
                  }
                </div>
              </FlexWrapper>
              <input ref={fileUpload} id={field.name + 'image'} style={{ opacity: 0 }} type={'file'} accept={isVideo ? acceptVideo : acceptImages} onChange={onFileSelect} />
            </label>
          }
        </>
      }
      {field.value && <ImageOptions>
        <p onClick={replaceFile}>Replace</p>
        <p onClick={removeFile}><Delete />Remove</p>
      </ImageOptions>}
      {isInvalid && !upImg && !isFileLoading && <Error>{get(form.errors, field.name)}</Error>}
    </ImageWrapper>
  );
}